import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import TipTapEditor from '@/components/TipTapEditor';
import MediaPickerModal from '@/components/MediaPickerModal';
import CommercialLabelSelect from '@/components/CommercialLabelSelect';
import OriginBadge from '@/components/OriginBadge';
import SchedulePanel, { fmtUkLong, relativeTo } from '@/components/SchedulePanel';
import { OrgMatchDialog, PersonMatchDialog, LocationMatchDialog } from '@/components/EntityMatchDialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  ArrowLeft, CheckCircle, XCircle, Send, BookOpen, BookX,
  Tag, Globe, User, Building2, LinkIcon, Clock, History,
  Edit3, AlertCircle, Sparkles, X, ImageIcon, MapPin, Plus,
  ExternalLink, Trash2, Rocket, Save, Lock, LockOpen,
  CalendarClock, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const statusStyles = {
  draft:       'bg-stone-100 text-stone-600 border border-stone-300',
  review:      'bg-amber-100 text-amber-700 border border-amber-300',
  refused:     'bg-red-100 text-red-700 border border-red-300',
  approved:    'bg-emerald-100 text-emerald-700 border border-emerald-300',
  published:   'bg-emerald-100 text-emerald-700 border border-emerald-300',
  unpublished: 'bg-zinc-100 text-zinc-600 border border-zinc-300',
  scheduled:   'bg-[#FFE4C4] text-[#1F1A17] border border-[#E7D2B8]',
};

const statusLabels = {
  draft: 'Draft', review: 'In Review', refused: 'Refused',
  approved: 'Published', published: 'Published', unpublished: 'Unpublished',
  scheduled: 'Scheduled',
};

const storyTypeLabels = {
  press_release:      'Press Release',
  executive_move:     'Executive Move',
  funding:            'Funding',
  partnership:        'Partnership',
  product_launch:     'Product Launch',
  regulation:         'Regulation',
  tender_procurement: 'Tender & Procurement',
  legal:              'Legal',
  event:              'Event',
  market_update:      'Market Update',
  other:              'Other',
};

const storyTypesList = Object.entries(storyTypeLabels).map(([value, label]) => ({ value, label }));

const HISTORY_LABELS = {
  create: 'Created', edit: 'Edited', submit: 'Submitted for review',
  approve: 'Approved & Published', refuse: 'Refused', publish: 'Republished',
  unpublish: 'Unpublished', 'save-draft': 'Saved to draft',
  delete: 'Moved to Deleted', restore: 'Restored', backdate: 'Public date changed',
  override: 'Okebourne override',
  eventflow_create: 'Created via Event Flow', auto_publish: 'Auto-published',
  schedule: 'Scheduled', reschedule: 'Rescheduled',
  'cancel-schedule': 'Schedule cancelled', 'retry-publish': 'Retry queued',
};

const actionVerbs = {
  approve:   'approved & published',
  publish:   'published',
  unpublish: 'unpublished',
  submit:    'submitted for review',
  refuse:    'refused',
  'save-draft': 'saved to draft',
  schedule:     'scheduled',
  reschedule:   'rescheduled',
  'cancel-schedule': 'taken off the schedule',
  'retry-publish':   'queued for retry',
};

const fmtDate = (val) => {
  if (!val) return '—';
  const normalised = val
    .replace(' ', 'T')
    .replace(/(\.\d{3})\d+/, '$1')
    .replace('+00:00', 'Z');
  const d = new Date(normalised);
  return isNaN(d.getTime()) ? val : d.toLocaleString();
};

const fmtUK = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (val) => (val ? new Date(val).toISOString() : null);

// Builds the correct PubLauncher URL: https://{domain}/{first-tag-slug}/{story-slug}
const getLiveUrl = (story) => {
  if (!story?.publication_domain || !story?.slug) return null;
  const tag = story.tags?.[0]
    ? story.tags[0].toLowerCase().replace(/\s+/g, '-')
    : 'news';
  return `https://${story.publication_domain}/${tag}/${story.slug}`;
};

export default function StoryDetailPage() {
  const { id }    = useParams();
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [story, setStory]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [tab, setTab] = useState(searchParams.get('tab') || 'content');

  const [clients, setClients]             = useState([]);
  const [publications, setPublications]   = useState([]);
  const [allTags, setAllTags]             = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [persons, setPersons]             = useState([]);
  const [bylines, setBylines]             = useState([]);
  const [storyTypes, setStoryTypes]       = useState([]);

  // Location state
  const [storyLocations, setStoryLocations]           = useState([]);
  const [locationMatchDialog, setLocationMatchDialog] = useState(false);
  const [orgMatchDialog, setOrgMatchDialog]           = useState(false);
  const [personMatchDialog, setPersonMatchDialog]     = useState(false);
  const [deleteDialog, setDeleteDialog]               = useState(false);

  const [showRefuseForm, setShowRefuseForm]   = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editorNotes, setEditorNotes]         = useState('');
  const [requiredChanges, setRequiredChanges] = useState('');

  const fetchStory = async () => {
    try {
      const [storyRes, locRes] = await Promise.allSettled([
        api.get(`/stories/${id}`),
        api.get(`/stories/${id}/locations`),
      ]);
      if (storyRes.status === 'fulfilled') {
        setStory(storyRes.value.data);
        setEditForm(storyRes.value.data);
      } else {
        toast.error('Failed to load story');
        navigate('/stories');
      }
      if (locRes.status === 'fulfilled') {
        setStoryLocations(locRes.value.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStory(); }, [id]);

  useEffect(() => {
    Promise.allSettled([
      api.get('/clients'), api.get('/publications'), api.get('/tags'),
      api.get('/organisations'), api.get('/persons'), api.get('/bylines'), api.get('/story-types'),
    ]).then(([c, p, t, o, pr, bl, st]) => {
      if (c.status  === 'fulfilled') setClients(c.value.data);
      if (p.status  === 'fulfilled') setPublications(p.value.data);
      if (t.status  === 'fulfilled') setAllTags(t.value.data);
      if (o.status  === 'fulfilled') setOrganisations(o.value.data);
      if (pr.status === 'fulfilled') setPersons(pr.value.data);
      if (bl.status === 'fulfilled') setBylines(bl.value.data);
      if (st.status === 'fulfilled') setStoryTypes(st.value.data);
    });
  }, []);

  const changeTab = (next) => {
    setTab(next);
    setSearchParams(next === 'content' ? {} : { tab: next }, { replace: true });
  };

  const dirtyRef = useRef(false);
  useEffect(() => {
    dirtyRef.current = editing && JSON.stringify(editForm) !== JSON.stringify(story);
  }, [editing, editForm, story]);
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#1F1A17] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!story) return null;

  const liveUrl  = getLiveUrl(story);
  const isEditor = user && ['editor', 'admin'].includes(user.role);
  const isAdmin  = user?.role === 'admin';
  const isOwner  = user && story.created_by_user_id === user.user_id;
  const canEdit  = isEditor || (user?.role === 'caster' && isOwner && ['draft', 'refused'].includes(story.status));
  const canSubmit = user?.role === 'caster' && isOwner && ['draft', 'refused'].includes(story.status);

  const updateField = (key, val) => setEditForm(f => ({ ...f, [key]: val }));

  const resetRefuseForm = () => {
    setShowRefuseForm(false);
    setRejectionReason('');
    setEditorNotes('');
    setRequiredChanges('');
  };

  const handleAction = async (action, data = {}) => {
    setSaving(true);
    try {
      const res = await api.post(`/stories/${id}/${action}`, data);
      setStory(res.data);
      setEditForm(res.data);
      toast.success(`Story ${actionVerbs[action] || action}`);
      resetRefuseForm();
      if (['approve', 'publish'].includes(action)) {
        try {
          const lr = await api.get(`/stories/${id}/locations`);
          setStoryLocations(lr.data || []);
        } catch { /* non-fatal */ }
      }
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const previousAssetId = story.media_asset_id;
      const res = await api.put(`/stories/${id}`, editForm);
      setStory(res.data);
      setEditForm(res.data);
      setEditing(false);

      if (editForm.media_asset_id && editForm.media_asset_id !== previousAssetId) {
        try {
          await api.post(`/media/assets/${editForm.media_asset_id}/usage`, {
            used_by_app:    'tempust',
            story_id:       id,
            publication_id: editForm.publication_id || null,
          });
        } catch { /* non-fatal */ }
      }

      toast.success('Story updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/stories/${id}`);
      toast.success('Story deleted');
      navigate('/stories');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
      setSaving(false);
    }
  };

  const toggleOverride = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/stories/${id}/override`, { locked: !story.editorial_locked });
      setStory(res.data);
      setEditForm(res.data);
      toast.success(res.data.editorial_locked ? 'Locked from Okebourne' : 'Unlocked — Okebourne may sync again');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change override');
    } finally {
      setSaving(false);
    }
  };



  const handleMediaSelected = ({ file_url, media_asset_id, caption, accreditation }) => {
    setEditForm(f => ({
      ...f,
      image:               file_url,
      media_asset_id:      media_asset_id,
      image_caption:       caption ?? '',
      image_accreditation: accreditation ?? '',
    }));
  }; 
  
  const handleOrgSelected = ({ id: orgId, name }) => {
    updateField('organisation_id', orgId);
    updateField('organisation_name', name);
    updateField('story_subject_type', 'organisation');
    updateField('person_id', null);
    updateField('person_name', '');
    setOrganisations(prev => prev.find(o => o.id === orgId) ? prev : [...prev, { id: orgId, name }]);
  };

  const handlePersonSelected = ({ id: personId, name }) => {
    updateField('person_id', personId);
    updateField('person_name', name);
    updateField('story_subject_type', 'person');
    updateField('organisation_id', null);
    updateField('organisation_name', '');
    setPersons(prev => prev.find(p => p.id === personId) ? prev : [...prev, { id: personId, name }]);
  };

  const handleLocationSelected = async ({ id: locationId, area_name, hub_name }) => {
    if (storyLocations.find(l => l.id === locationId)) return;
    try {
      await api.post(`/stories/${id}/locations`, { location_id: locationId, auto_tagged: false });
      setStoryLocations(prev => [...prev, { id: locationId, area_name, hub_name, auto_tagged: false }]);
      toast.success(`Location "${area_name}" added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add location');
    }
  };

  const handleRemoveLocation = async (locationId) => {
    try {
      await api.delete(`/stories/${id}/locations/${locationId}`);
      setStoryLocations(prev => prev.filter(l => l.id !== locationId));
      toast.success('Location removed');
    } catch {
      toast.error('Failed to remove location');
    }
  };

  const label = 'text-sm text-[#6E6258] font-medium mb-1.5 block';

  const selectedClient = clients.find(c => c.name === editForm.client_name);
  const selectedOrg    = organisations.find(o => o.id === editForm.organisation_id);
  const selectedPerson = persons.find(p => p.id === editForm.person_id);
  const pubTags        = allTags.filter(t => t.publication_id === editForm.publication_id);
  const availableTags  = pubTags.filter(t => !(editForm.tags || []).includes(t.name));

  const addTag    = (name) => updateField('tags', [...(editForm.tags || []), name]);
  const removeTag = (name) => updateField('tags', (editForm.tags || []).filter(x => x !== name));

  const finput = 'w-full bg-white text-[#1F1A17] border border-border rounded-md h-10 px-3 text-sm placeholder-[#9C8E84] focus:outline-none focus:border-[#1F1A17]';

  const tabTrigger = 'data-[state=active]:bg-[#FFE4C4] data-[state=active]:text-[#1F1A17] text-muted-foreground';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="story-detail-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (dirtyRef.current && !window.confirm('You have unsaved changes. Leave without saving?')) return;
              navigate('/stories');
            }}
            className="text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8]"
            data-testid="back-to-stories"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground font-outfit">{story.headline || 'Untitled'}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              by {story.created_by_name} &middot; {fmtDate(story.created_at)}
              {story.approved_by_name && <> &middot; Editor: {story.approved_by_name}</>}
              {(story.public_display_at || story.published_at) && <> &middot; Public: {fmtUK(story.public_display_at || story.published_at)}</>}
              {story.status === 'scheduled' && story.scheduled_for && (
                <> &middot; Publishing {fmtUkLong(story.scheduled_for)} ({relativeTo(story.scheduled_for)})</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {story.status === 'published' && liveUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(liveUrl, '_blank', 'noopener,noreferrer')}
              className="text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 gap-1.5"
            >
              <ExternalLink size={13} />
              View Live
            </Button>
          )}
          <OriginBadge origin={story.story_origin} size="md" />
          {story.commercial_label && (
            <span className="inline-flex items-center rounded-full text-xs px-2 py-1 bg-[#FFF4E8] text-[#6E6258] border border-[#E7D2B8]">
              {story.commercial_label}
            </span>
          )}
          {story.editorial_locked && (
            <span className="inline-flex items-center gap-1 rounded-full text-xs px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200" title="Okebourne won't auto-update this story">
              <Lock size={12} /> Locked
            </span>
          )}
          <Badge
            className={`text-sm px-3 py-1 ${statusStyles[story.status] || statusStyles.draft}`}
            data-testid="story-status-badge"
          >
            {statusLabels[story.status] || story.status}
          </Badge>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="py-3 px-5 flex items-center gap-4">
          {story.completeness_score >= 80
            ? <CheckCircle size={18} className="text-emerald-600" />
            : <AlertCircle size={18} className="text-amber-600" />
          }
          <span className="text-sm text-muted-foreground">Completeness</span>
          <Progress value={story.completeness_score} className="flex-1 h-2 bg-[#F8EBD9]" />
          <span className={`text-sm font-bold ${story.completeness_score >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>
            {story.completeness_score}%
          </span>
        </CardContent>
      </Card>

      {(canEdit || isEditor || canSubmit) && (
        <Card className="border-border">
          <CardContent className="py-3 px-5 flex items-center gap-2 flex-wrap">
            {canEdit && !editing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setEditing(true); setEditForm(story); }}
                className="border-border text-foreground hover:bg-[#FFF4E8]"
                data-testid="edit-story-btn"
              >
                <Edit3 size={14} className="mr-1.5" />Edit
              </Button>
            )}
            {editing && (
              <>
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                  data-testid="save-edit-btn"
                >
                  <Save size={14} className="mr-1.5" />{saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEditing(false); setEditForm(story); }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </Button>
              </>
            )}

            {!editing && isEditor && story.status === 'review' && !showRefuseForm && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleAction('approve')}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="approve-publish-btn"
                >
                  <CheckCircle size={14} className="mr-1.5" />Approve &amp; Publish
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setShowRefuseForm(true)}
                  data-testid="refuse-btn"
                >
                  <XCircle size={14} className="mr-1.5" />Refuse
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction('save-draft')}
                  disabled={saving}
                  className="border-border text-foreground hover:bg-[#FFF4E8]"
                  data-testid="save-draft-btn"
                >
                  <Save size={14} className="mr-1.5" />Save as Draft
                </Button>
              </>
            )}
            {!editing && isEditor && story.status === 'review' && showRefuseForm && (
              <Button size="sm" variant="ghost" onClick={resetRefuseForm} className="text-muted-foreground hover:text-foreground">
                <X size={14} className="mr-1.5" />Cancel Refusal
              </Button>
            )}
            {!editing && isEditor && story.status === 'approved' && (
              <Button
                size="sm"
                onClick={() => handleAction('publish')}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="publish-btn"
              >
                <BookOpen size={14} className="mr-1.5" />Publish
              </Button>
            )}
            {!editing && isEditor && story.status === 'unpublished' && (
              <Button
                size="sm"
                onClick={() => handleAction('publish')}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="republish-btn"
              >
                <Rocket size={14} className="mr-1.5" />Republish
              </Button>
            )}

            {!editing && isEditor && ['review', 'approved', 'unpublished', 'scheduled'].includes(story.status) && !showRefuseForm && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => changeTab('schedule')}
                disabled={saving}
                className={story.last_publish_error
                  ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                  : 'border-border text-foreground hover:bg-[#FFF4E8]'}
                data-testid="schedule-btn"
              >
                {story.last_publish_error
                  ? <><AlertTriangle size={14} className="mr-1.5" />Publish failed</>
                  : <><CalendarClock size={14} className="mr-1.5" />{story.status === 'scheduled' ? 'Manage schedule' : 'Schedule'}</>
                }
              </Button>
            )}

            {!editing && isEditor && ['refused', 'draft'].includes(story.status) && (
              <Button
                size="sm"
                onClick={() => handleAction('submit')}
                disabled={saving}
                className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                data-testid="reopen-btn"
              >
                <Send size={14} className="mr-1.5" />{story.status === 'draft' ? 'Submit for Review' : 'Reopen for Review'}
              </Button>
            )}
            {!editing && !isEditor && canSubmit && (
              <Button
                size="sm"
                onClick={() => handleAction('submit')}
                disabled={saving}
                className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                data-testid="submit-btn"
              >
                <Send size={14} className="mr-1.5" />Submit for Review
              </Button>
            )}

            {!editing && isEditor && story.story_origin === 'eventflow' && (
              <Button
                size="sm"
                variant="outline"
                onClick={toggleOverride}
                disabled={saving}
                className={story.editorial_locked
                  ? 'text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100'
                  : 'border-border text-foreground hover:bg-[#FFF4E8]'}
                data-testid="override-btn"
                title="When locked, Okebourne won't auto-update or republish this story"
              >
                {story.editorial_locked
                  ? <><Lock size={14} className="mr-1.5" />Locked from Okebourne</>
                  : <><LockOpen size={14} className="mr-1.5" />Lock from Okebourne</>}
              </Button>
            )}

            {!editing && isEditor && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDeleteDialog(true)}
                disabled={saving}
                className="text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200 ml-auto"
                data-testid="delete-btn"
              >
                <Trash2 size={14} className="mr-1.5" />Delete
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {showRefuseForm && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm text-red-700 flex items-center gap-2">
              <XCircle size={15} className="text-red-500" />
              Refuse Story
            </CardTitle>
            <p className="text-xs text-red-500 mt-0.5">Fill in the feedback below — the caster will see this when their story is returned.</p>
          </CardHeader>
          <CardContent className="space-y-3 px-5 pb-5">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">
                Refusal Reason <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                placeholder="Why is this story being refused?"
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[70px] resize-none"
                data-testid="rejection-reason-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">Editor Notes</label>
              <Textarea
                value={editorNotes}
                onChange={e => setEditorNotes(e.target.value)}
                placeholder="Additional context or guidance for the caster..."
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[70px] resize-none"
                data-testid="editor-notes-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">Required Changes</label>
              <Textarea
                value={requiredChanges}
                onChange={e => setRequiredChanges(e.target.value)}
                placeholder="List the specific changes needed before resubmission..."
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[70px] resize-none"
                data-testid="required-changes-input"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleAction('refuse', {
                  reason:           rejectionReason,
                  editor_notes:     editorNotes,
                  required_changes: requiredChanges,
                })}
                disabled={!rejectionReason.trim() || saving}
                data-testid="confirm-refuse-btn"
              >
                {saving ? 'Refusing...' : 'Confirm Refusal'}
              </Button>
              <Button size="sm" variant="ghost" onClick={resetRefuseForm} className="text-muted-foreground hover:text-foreground">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {story.status === 'refused' && (story.rejection_reason || story.editor_notes || story.required_changes) && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <XCircle size={16} className="text-red-500" />
              <span className="text-sm font-semibold text-red-700">Story Refused — Feedback from Editor</span>
            </div>
            {story.rejection_reason && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-[#9C8E84] uppercase tracking-wide">Refusal Reason</p>
                <p className="text-sm text-red-600 leading-relaxed">{story.rejection_reason}</p>
              </div>
            )}
            {story.editor_notes && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-[#9C8E84] uppercase tracking-wide">Editor Notes</p>
                <p className="text-sm text-amber-700 leading-relaxed">{story.editor_notes}</p>
              </div>
            )}
            {story.required_changes && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-[#9C8E84] uppercase tracking-wide">Required Changes</p>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{story.required_changes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList className="bg-[#FFF4E8] border border-border">
          <TabsTrigger value="content"  className={tabTrigger}>Content</TabsTrigger>
          <TabsTrigger value="metadata" className={tabTrigger}>Metadata</TabsTrigger>
          <TabsTrigger value="schedule" className={tabTrigger}>
            Schedule
            {story.status === 'scheduled' && (
              <span className={`ml-1.5 w-1.5 h-1.5 rounded-full ${story.last_publish_error ? 'bg-red-500' : 'bg-emerald-500'}`} />
            )}
          </TabsTrigger>
          <TabsTrigger value="intelligence" className={tabTrigger}>Intelligence</TabsTrigger>
          <TabsTrigger value="history"      className={tabTrigger}>History</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-4 space-y-4">
          {!editing && story.image && (
            <div className="max-w-md">
              <img
                src={story.image}
                alt=""
                className="w-full rounded-xl aspect-video object-cover"
                data-testid="story-detail-image"
              />
              {(story.image_caption || story.image_accreditation) && (
                <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
                  {story.image_caption       && <span>{story.image_caption}</span>}
                  {story.image_accreditation && <span>Credit: {story.image_accreditation}</span>}
                </div>
              )}
            </div>
          )}

          {editing ? (
            <div className="space-y-4">
              <div>
                <label className={label}>Featured Image</label>
                {editForm.image ? (
                  <div className="relative max-w-sm">
                    <img
                      src={editForm.image}
                      alt="Featured"
                      className="w-full rounded-lg aspect-video object-cover"
                    />
                    <div className="absolute top-2 right-2 flex gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="bg-black/40 hover:bg-black/60 text-white h-8 w-8"
                        onClick={() => setMediaPickerOpen(true)}
                        title="Change image"
                      >
                        <ImageIcon size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="bg-black/40 hover:bg-black/60 text-white h-8 w-8"
                        onClick={() => setEditForm(f => ({ ...f, image: null, media_asset_id: null, image_caption: '', image_accreditation: '' }))}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                    <div className="flex gap-3 mt-2">
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground mb-1 block">Caption</label>
                        <Input
                          value={editForm.image_caption || ''}
                          onChange={e => setEditForm(f => ({ ...f, image_caption: e.target.value }))}
                          placeholder="Image caption"
                          className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground mb-1 block">Accreditation</label>
                        <Input
                          value={editForm.image_accreditation || ''}
                          onChange={e => setEditForm(f => ({ ...f, image_accreditation: e.target.value }))}
                          placeholder="Photo credit"
                          className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setMediaPickerOpen(true)}
                    className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-[#E7D2B8] rounded-lg cursor-pointer hover:border-[#C9B49A] hover:bg-[#FFF4E8] transition-colors w-full max-w-sm min-h-[120px]"
                  >
                    <ImageIcon size={24} className="text-[#C9B49A] mb-2" />
                    <span className="text-sm text-muted-foreground">Select from media library</span>
                  </button>
                )}
              </div>

              <div>
                <label className={label}>Headline</label>
                <Input
                  value={editForm.headline || ''}
                  onChange={e => setEditForm({ ...editForm, headline: e.target.value })}
                  className="bg-white text-[#1F1A17] border-border text-lg font-semibold"
                />
              </div>
              <div>
                <label className={label}>Intro</label>
                <Textarea
                  value={editForm.lede || ''}
                  onChange={e => setEditForm({ ...editForm, lede: e.target.value })}
                  className="bg-white text-[#1F1A17] border-border min-h-[80px]"
                />
              </div>
              <div>
                <label className={label}>Body</label>
                <TipTapEditor
                  content={editForm.body || ''}
                  onChange={val => setEditForm({ ...editForm, body: val })}
                  mediaContext={{
                    organisationId:   editForm.organisation_id,
                    organisationName: editForm.organisation_name,
                    personId:         editForm.person_id,
                    personName:       editForm.person_name,
                    tags:             editForm.tags,
                    publicationId:    editForm.publication_id,
                    organisations,
                  }}
                />
              </div>
            </div>
          ) : (
            <Card className="border-border">
              <CardContent className="p-6 space-y-4">
                {story.lede && (
                  <p className="text-lg text-[#2B241F] italic leading-relaxed font-merriweather">{story.lede}</p>
                )}
                {story.body && (
                  <div
                    className="story-body-preview text-[#1F1A17]"
                    dangerouslySetInnerHTML={{ __html: story.body }}
                    data-testid="story-body-content"
                  />
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="metadata" className="mt-4">
          {editing ? (
            <div className="space-y-4">
              <Card className="border-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground font-outfit">Classification &amp; Distribution</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>Client</label>
                      <Select value={editForm.client_name || ''} onValueChange={v => { updateField('client_name', v); updateField('project_name', ''); }}>
                        <SelectTrigger className="bg-white text-[#1F1A17] border-border"><SelectValue placeholder="Select client" /></SelectTrigger>
                        <SelectContent className="bg-white border-border">{clients.map(c => <SelectItem key={c.id} value={c.name} className="text-[#1F1A17]">{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={label}>Project</label>
                      <Select value={editForm.project_name || ''} onValueChange={v => updateField('project_name', v)} disabled={!selectedClient}>
                        <SelectTrigger className="bg-white text-[#1F1A17] border-border"><SelectValue placeholder="Select project" /></SelectTrigger>
                        <SelectContent className="bg-white border-border">{selectedClient?.projects?.map(p => <SelectItem key={p} value={p} className="text-[#1F1A17]">{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={label}>Publication</label>
                      <Select value={editForm.publication_id || ''} onValueChange={v => updateField('publication_id', v)}>
                        <SelectTrigger className="bg-white text-[#1F1A17] border-border"><SelectValue placeholder="Select publication" /></SelectTrigger>
                        <SelectContent className="bg-white border-border">{publications.map(pub => <SelectItem key={pub.id} value={pub.id} className="text-[#1F1A17]">{pub.name}{pub.domain && ` (${pub.domain})`}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={label}>Story Type</label>
                      <Select value={editForm.story_type || 'other'} onValueChange={v => updateField('story_type', v)}>
                        <SelectTrigger className="bg-white text-[#1F1A17] border-border"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-border">{(storyTypes.length > 0 ? storyTypes : storyTypesList).map(t => <SelectItem key={t.value} value={t.value} className="text-[#1F1A17]">{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={label}>Byline</label>
                      <Select value={editForm.byline_id || ''} onValueChange={v => updateField('byline_id', v)}>
                        <SelectTrigger className="bg-white text-[#1F1A17] border-border"><SelectValue placeholder="Select byline" /></SelectTrigger>
                        <SelectContent className="bg-white border-border">{bylines.map(b => <SelectItem key={b.id} value={b.id} className="text-[#1F1A17]">{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <CommercialLabelSelect
                        value={editForm.commercial_label_id}
                        onChange={v => updateField('commercial_label_id', v)}
                        canManage={isAdmin}
                        showHeader={false}
                      />
                    </div>
                    <div>
                      <label className={label}>Geography</label>
                      <Input value={editForm.geography || ''} onChange={e => updateField('geography', e.target.value)} placeholder="e.g. Europe, Global..." className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
                    </div>
                  </div>

                  <div>
                    <label className={label}>Tags</label>
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      {(editForm.tags || []).length > 0 ? (editForm.tags || []).map(t => (
                        <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F8EBD9] border border-[#E7D2B8] text-xs text-[#1F1A17]">
                          {t}
                          <button onClick={() => removeTag(t)} className="text-[#9C8E84] hover:text-red-500"><X size={11} /></button>
                        </span>
                      )) : <span className="text-xs text-muted-foreground">No tags yet</span>}
                    </div>
                    <Select value="" onValueChange={v => v && addTag(v)} disabled={!editForm.publication_id}>
                      <SelectTrigger className="bg-white text-[#1F1A17] border-border"><SelectValue placeholder={editForm.publication_id ? 'Add a tag' : 'Select a publication first'} /></SelectTrigger>
                      <SelectContent className="bg-white border-border">
                        {availableTags.length > 0
                          ? availableTags.map(t => <SelectItem key={t.id} value={t.name} className="text-[#1F1A17]">{t.name}</SelectItem>)
                          : <div className="px-2 py-1.5 text-xs text-muted-foreground">No more tags for this publication</div>
                        }
                      </SelectContent>
                    </Select>
                  </div>

                  {isEditor && (
                    <div>
                      <label className={label}>Public date &amp; time</label>
                      <input
                        type="datetime-local"
                        value={toLocalInput(editForm.public_display_at)}
                        onChange={e => updateField('public_display_at', fromLocalInput(e.target.value))}
                        className={finput}
                        data-testid="public-date-input"
                      />
                      <p className="text-[11px] text-[#9C8E84] mt-1">The date shown on the live site. Editors can backdate this.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground font-outfit">Source</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={label}>Source Link</label><Input value={editForm.source_link || ''} onChange={e => updateField('source_link', e.target.value)} placeholder="https://..." className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" /></div>
                    <div><label className={label}>Source Name</label><Input value={editForm.source_name || ''} onChange={e => updateField('source_name', e.target.value)} className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" /></div>
                    <div><label className={label}>Read Time (min)</label><Input type="number" min="1" value={editForm.estimated_read_time || ''} onChange={e => updateField('estimated_read_time', e.target.value ? parseInt(e.target.value) : null)} className="bg-white text-[#1F1A17] border-border" /></div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground font-outfit">Story Subject</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Button variant={editForm.story_subject_type === 'organisation' ? 'default' : 'outline'} size="sm"
                      onClick={() => { updateField('story_subject_type', 'organisation'); updateField('person_id', null); updateField('person_name', ''); }}
                      className={editForm.story_subject_type === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] hover:bg-[#2B241F]' : 'bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground'}>
                      Organisation
                    </Button>
                    <Button variant={editForm.story_subject_type === 'person' ? 'default' : 'outline'} size="sm"
                      onClick={() => { updateField('story_subject_type', 'person'); updateField('organisation_id', null); updateField('organisation_name', ''); }}
                      className={editForm.story_subject_type === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] hover:bg-[#2B241F]' : 'bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground'}>
                      Person
                    </Button>
                  </div>

                  {editForm.story_subject_type !== 'person' && (
                    <div>
                      <label className={label}>Organisation</label>
                      <div className="flex gap-2 items-center">
                        {selectedOrg ? (
                          <div className="flex-1 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{selectedOrg.name}</p>
                              {selectedOrg.aliases?.length > 0 && (
                                <p className="text-xs text-[#9C8E84]">aka {selectedOrg.aliases.slice(0, 2).join(', ')}</p>
                              )}
                            </div>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-[#9C8E84] hover:text-red-500 hover:bg-red-50"
                              onClick={() => { updateField('organisation_id', null); updateField('organisation_name', ''); }}>
                              <X size={13} />
                            </Button>
                          </div>
                        ) : (
                          <Select value={editForm.organisation_id || ''} onValueChange={v => {
                            const org = organisations.find(o => o.id === v);
                            updateField('organisation_id', v);
                            if (org) updateField('organisation_name', org.name);
                          }}>
                            <SelectTrigger className="bg-white text-[#1F1A17] border-border flex-1"><SelectValue placeholder="Select organisation" /></SelectTrigger>
                            <SelectContent className="bg-white border-border">{organisations.map(o => <SelectItem key={o.id} value={o.id} className="text-[#1F1A17]">{o.name}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        <Button variant="outline" size="icon" onClick={() => setOrgMatchDialog(true)} className="bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground shrink-0" title="Find or add organisation">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {editForm.story_subject_type === 'person' && (
                    <div>
                      <label className={label}>Person</label>
                      <div className="flex gap-2 items-center">
                        {selectedPerson ? (
                          <div className="flex-1 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{selectedPerson.name}</p>
                              {selectedPerson.title && <p className="text-xs text-[#9C8E84]">{selectedPerson.title}</p>}
                            </div>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-[#9C8E84] hover:text-red-500 hover:bg-red-50"
                              onClick={() => { updateField('person_id', null); updateField('person_name', ''); }}>
                              <X size={13} />
                            </Button>
                          </div>
                        ) : (
                          <Select value={editForm.person_id || ''} onValueChange={v => {
                            const person = persons.find(p => p.id === v);
                            updateField('person_id', v);
                            if (person) updateField('person_name', person.name);
                          }}>
                            <SelectTrigger className="bg-white text-[#1F1A17] border-border flex-1"><SelectValue placeholder="Select person" /></SelectTrigger>
                            <SelectContent className="bg-white border-border">{persons.map(p => <SelectItem key={p.id} value={p.id} className="text-[#1F1A17]">{p.name}{p.title ? ` - ${p.title}` : ''}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        <Button variant="outline" size="icon" onClick={() => setPersonMatchDialog(true)} className="bg-transparent border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground shrink-0" title="Find or add person">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground font-outfit flex items-center gap-1.5"><MapPin size={14} /> Local Areas</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Tagged areas</span>
                    <Button variant="ghost" size="sm" onClick={() => setLocationMatchDialog(true)} className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8]">
                      <Plus size={11} className="mr-1" /> Add
                    </Button>
                  </div>
                  {storyLocations.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {storyLocations.map(loc => (
                        <span key={loc.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFF4E8] border border-[#E7D2B8] text-xs text-[#1F1A17]">
                          {loc.area_name}
                          {loc.hub_name && <span className="text-[#9C8E84]">· {loc.hub_name}</span>}
                          {loc.auto_tagged && <span className="text-[#9C8E84] text-[10px]">auto</span>}
                          <button onClick={() => handleRemoveLocation(loc.id)} className="text-[#9C8E84] hover:text-red-500 ml-0.5"><X size={11} /></button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No locations tagged</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  {[
                    { icon: Building2, label: 'Client / Project', value: `${story.client_name || '—'}${story.project_name ? ' / ' + story.project_name : ''}` },
                    { icon: Tag,       label: 'Story Type',       value: storyTypeLabels[story.story_type] || story.story_type },
                    { icon: Globe,     label: 'Geography',        value: story.geography || '—' },
                    { icon: Globe,     label: 'Publication',      value: story.publication_name || '—' },
                    { icon: User,      label: 'Byline',           value: story.byline_name || '—' },
                    { icon: Tag,       label: 'Commercial Label', value: story.commercial_label || '—' },
                    { icon: User,      label: 'Person',           value: story.person_name || '—' },
                    { icon: Building2, label: 'Organisation',     value: story.organisation_name || '—' },
                    { icon: LinkIcon,  label: 'Source',           value: story.source_name || '—', link: story.source_link },
                    { icon: Clock,     label: 'Read Time',        value: story.estimated_read_time ? `${story.estimated_read_time} min` : '—' },
                    { icon: Clock,     label: 'Public Date',      value: fmtUK(story.public_display_at || story.published_at) },
                    { icon: Clock,     label: 'Created',          value: fmtUK(story.created_at) },
                  ].map(({ icon: Icon, label, value, link }) => (
                    <div key={label} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                      <Icon size={16} className="text-[#C9B49A] mt-0.5 shrink-0" />
                      <div>
                        <span className="text-[#9C8E84] text-xs block">{label}</span>
                        {link
                          ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{value}</a>
                          : <span className="text-foreground">{value}</span>
                        }
                      </div>
                    </div>
                  ))}
                </div>

                {story.tags?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <span className="text-xs text-muted-foreground block mb-2">Tags</span>
                    <div className="flex flex-wrap gap-2">
                      {story.tags.map(t => (
                        <Badge key={t} className="bg-[#F8EBD9] text-[#6E6258] border-[#E7D2B8]">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {story.status === 'published' && liveUrl && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <span className="text-xs text-muted-foreground block mb-1.5">Live URL</span>
                    <a
                      href={liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline break-all font-mono"
                    >
                      {liveUrl}
                    </a>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <MapPin size={12} /> Locations
                    </span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setLocationMatchDialog(true)}
                        className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8]"
                      >
                        <Plus size={11} className="mr-1" /> Add
                      </Button>
                    )}
                  </div>
                  {storyLocations.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {storyLocations.map(loc => (
                        <span
                          key={loc.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFF4E8] border border-[#E7D2B8] text-xs text-[#1F1A17]"
                        >
                          {loc.area_name}
                          {loc.hub_name && <span className="text-[#9C8E84]">· {loc.hub_name}</span>}
                          {loc.auto_tagged && <span className="text-[#9C8E84] text-[10px]">auto</span>}
                          {canEdit && (
                            <button
                              onClick={() => handleRemoveLocation(loc.id)}
                              className="text-[#9C8E84] hover:text-red-500 ml-0.5"
                            >
                              <X size={11} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No locations tagged</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="schedule" className="mt-4">
          <SchedulePanel
            story={story}
            canSchedule={isEditor}
            saving={saving}
            onAction={handleAction}
          />
        </TabsContent>

        <TabsContent value="intelligence" className="mt-4">
          <Card className="border-border">
            <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
              <Sparkles size={28} className="text-[#C9B49A]" />
              <p className="text-sm font-medium text-foreground">Intelligence</p>
              <p className="text-xs text-muted-foreground">Coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {story.versions?.length > 0 ? (
            <div className="space-y-3">
              {[...story.versions].reverse().map((v, i) => (
                <Card key={i} className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                      <History size={14} className="text-[#C9B49A]" />
                      <span className="text-sm font-medium text-foreground">
                        v{v.version} &middot; {HISTORY_LABELS[v.action] || v.action}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        by {v.changed_by_name} &middot; {fmtUK(v.changed_at)}
                      </span>
                    </div>
                    {v.details?.changed?.length > 0 && (
                      <p className="text-xs text-[#6E6258]">Changed: {v.details.changed.join(', ')}</p>
                    )}
                    {v.details?.public_display_at && (
                      <p className="text-xs text-[#6E6258]">
                        Public date: {fmtUK(v.details.public_display_at.from) } &rarr; {fmtUK(v.details.public_display_at.to)}
                      </p>
                    )}
                    {typeof v.details?.scheduled_for === 'string' && (
                      <p className="text-xs text-[#6E6258]">Scheduled for: {fmtUK(v.details.scheduled_for)}</p>
                    )}
                    {v.details?.scheduled_for?.to && (
                      <p className="text-xs text-[#6E6258]">
                        Scheduled: {fmtUK(v.details.scheduled_for.from)} &rarr; {fmtUK(v.details.scheduled_for.to)}
                      </p>
                    )}
                    {v.details?.cancelled_schedule && (
                      <p className="text-xs text-[#6E6258]">Was scheduled for {fmtUK(v.details.cancelled_schedule)}</p>
                    )}
                    {v.details?.rejection_reason && (
                      <p className="text-xs text-red-600">Reason: {v.details.rejection_reason}</p>
                    )}
                    {!v.details && v.headline && (
                      <p className="text-xs text-muted-foreground truncate">Headline: {v.headline}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No version history yet</p>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2"><Trash2 size={18} className="text-red-500" />Delete Story Permanently</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This action cannot be undone. The story "{story.headline || 'Untitled'}" and all its version history will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog(false)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-btn">{saving ? 'Deleting...' : 'Delete Permanently'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaPickerModal
        open={mediaPickerOpen}
        onOpenChange={setMediaPickerOpen}
        organisationId={editForm.organisation_id}
        tags={editForm.tags}
        publicationId={editForm.publication_id}
        currentAssetId={editForm.media_asset_id}
        onSelect={handleMediaSelected}
      />

      <OrgMatchDialog
        open={orgMatchDialog}
        onOpenChange={setOrgMatchDialog}
        onSelect={handleOrgSelected}
        organisations={organisations}
        setOrganisations={setOrganisations}
      />
      <PersonMatchDialog
        open={personMatchDialog}
        onOpenChange={setPersonMatchDialog}
        onSelect={handlePersonSelected}
        organisations={organisations}
        persons={persons}
        setPersons={setPersons}
      />
      <LocationMatchDialog
        open={locationMatchDialog}
        onOpenChange={setLocationMatchDialog}
        onSelect={handleLocationSelected}
        setLocations={() => {}}
        publicationId={editForm.publication_id || story.publication_id || null}
      />
    </div>
  );
}